import { FC } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Nullable } from "@ctra/utils";
import { useGoogleAnalytics } from "@ctra/analytics";
import { MediaQueryDynamicImport } from "@ctra/components";
import { Enterprise, EnterpriseAppState, GamePlanEntity, GamePlans } from "@ctra/api";

import { GAActions, testIds } from "@game-plans";
import { CompiledRoutes } from "@routes";
import { ScorecardContext } from "@scorecard";

/**
 * GamePlanDetails component
 * @constructor
 */
export const GamePlanDetails: FC = () => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { trackEvent } = useGoogleAnalytics();
  const searchParams = new URLSearchParams(search);

  /**
   * Get params: gamePlanID, dataDescriptorID and farmID
   */
  const gamePlanID: Nullable<GamePlanEntity["id"]> = searchParams.get("gamePlanID");

  /**
   * get game plan details
   */
  const gamePlan = useSelector<EnterpriseAppState, Nullable<GamePlanEntity>>((state) =>
    gamePlanID ? Enterprise.entities.getGamePlan(state, { gamePlanID }) : void 0
  );

  /**
   * Handle deleting game plan
   * @note no coverage expectation because its already being tested within GamePlan.test.tsx
   */
  const onDelete = /* istanbul ignore next */ () => {
    trackEvent(GAActions.deleteGamePlan);
    gamePlanID && dispatch(GamePlans.actions.deleteGamePlan.start(gamePlanID));
    Enterprise.history.push({ pathname, search: "" });
  };

  /**
   * Handle editing game plan
   * @note no coverage expectation because its already being tested within GamePlan.test.tsx
   */
  const onEdit = /* istanbul ignore next */ () => {
    trackEvent(GAActions.editGamePlan);
    Enterprise.history.push(CompiledRoutes.app.gamePlans.gamePlan.edit.index({ gamePlanID }));
  };

  return gamePlan ? (
    <div data-testid={testIds.entity.details.wrapper}>
      <MediaQueryDynamicImport mobile={() => import("./Mobile")} desktop={() => import("./Desktop")}>
        {(Details) => (
          <ScorecardContext.Provider dataDescriptorID={gamePlan.dataDescriptorID} farmID={+gamePlan.farmID}>
            <Details onDelete={onDelete} onEdit={onEdit} {...gamePlan} />
          </ScorecardContext.Provider>
        )}
      </MediaQueryDynamicImport>
    </div>
  ) : null;
};
